import React from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';
import { FormattedMessage } from 'react-intl';
import BuyerAssurance from 'dibs-vg/dist/react/buyer-assurance';
import classnames from 'classnames';
import {
    getTradeBuyerProtectionDuration,
    getTradeBuyerProtectionTier,
    getTradeUserRewardsProgramInfo,
} from '../helpers/tradeBuyerProtectionHelper';
import styles from './PdpSharedBuyerProtectionBox.scss';

export const PdpSharedBuyerProtectionBoxComponent = ({ isTrade, withImage, user }) => {
    const readMoreHref = isTrade ? '/info/trade/promise/' : '/about/promise/';
    const {
        tradeProtectionTierCode,
        tradeProtectionDurationCode,
        showTradeRewardsCopy,
    } = getTradeUserRewardsProgramInfo(user);

    const wrapperClassName = classnames(styles.wrapper, {
        [styles.isFixed]: withImage,
        rowFlex: withImage,
    });
    const copyClassName = classnames(styles.copy, {
        colXs9: withImage,
    });

    return (
        <div className={wrapperClassName} data-tn="buyer-protection-wrrapper">
            {withImage && <div className={`${styles.image} colXs3`} />}
            <div className={copyClassName}>
                <div className={styles.title} data-tn="buyer-protection-title">
                    <BuyerAssurance className={styles.icon} />
                    <FormattedMessage
                        id="PdpSharedBuyerProtectionBox.title"
                        defaultMessage="Buyer Protection Guaranteed"
                    />
                </div>
                <div
                    className={classnames(styles.message, {
                        [styles.tradeRewardsMessage]: showTradeRewardsCopy,
                    })}
                    data-tn="buyer-protection-message"
                >
                    {!showTradeRewardsCopy && (
                        <span className={styles.messageBold}>
                            <FormattedMessage
                                id="PdpSharedBuyerProtectionBox.promiseMessage"
                                defaultMessage="Our Promise To You:"
                            />
                        </span>
                    )}{' '}
                    {showTradeRewardsCopy ? (
                        <FormattedMessage
                            id="PdpSharedBuyerProtectionBox.tradeRewardsMessage"
                            defaultMessage="If your order doesn't arrive as described, we'll mediate between you and the seller to make it right. As a {tier}, you get {days} to report any issues you have with the order."
                            values={{
                                tier: (
                                    <span className={styles.messageBold}>
                                        {getTradeBuyerProtectionTier(tradeProtectionTierCode)}
                                    </span>
                                ),
                                days: (
                                    <span className={styles.messageBold}>
                                        {getTradeBuyerProtectionDuration(
                                            tradeProtectionDurationCode
                                        )}
                                    </span>
                                ),
                            }}
                        />
                    ) : (
                        <FormattedMessage
                            id="PdpSharedBuyerProtectionBox.message"
                            defaultMessage="If you're not happy with the way an item arrived, we'll work with you and the seller to reach an optimal resolution."
                        />
                    )}{' '}
                    <a
                        href={readMoreHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-tn="buyer-protection-link"
                    >
                        <FormattedMessage
                            id="PdpSharedBuyerProtectionBox.readMoreLink"
                            defaultMessage="Read more"
                        />
                    </a>
                </div>
            </div>
        </div>
    );
};

PdpSharedBuyerProtectionBoxComponent.propTypes = {
    isTrade: PropTypes.bool,
    withImage: PropTypes.bool,
    user: PropTypes.object,
};

export const PdpSharedBuyerProtectionBox = createFragmentContainer(
    PdpSharedBuyerProtectionBoxComponent,
    {
        user: graphql`
            fragment PdpSharedBuyerProtectionBox_user on User {
                tradeFirm @include(if: $isTrade) {
                    rewards {
                        currentProgram {
                            programInfo {
                                code
                            }
                            childPrograms(filter: "ENHANCED_BUYER_PROTECTION") {
                                code
                            }
                        }
                    }
                }
            }
        `,
    }
);
